import React, { useEffect } from 'react'
import { type APIRequest, useApi } from './use-api-requests.hook'
import { useAppContext } from './use-app-context'
import { Actions, type Step ,Steps } from '@store/reducer'
import { type State } from '@store/defaultReducerState.ts'
import { getFormattedDate } from '@utils/dates'
import { type DateValue } from 'react-aria'
import { forceScrollHeight } from './use-resize'
import { log } from '@utils/debug'
import type { Survey } from '@ui/richiesta/pages/servizi.hooks'
import { useAtom } from 'jotai'
import { currentStepAtom } from '@store/atoms'
import type { Installment } from './type-ds'


function defaultWhenEmpty(
	currentStep: Step,
	target: string | null,
	result: string
) {
	log('defaultWhenEmpty', target)
	if (target === null || (currentStep <= Steps.DATI_ANAGRAFICI && target === '')) {
		return ''
	}

	return target.trim() === '' ? result : target
}

export function formatToVMModelDate(date: DateValue | string | null) {
	if (date === null) {
		return ''
	}

	if (typeof date === 'string') {
		// ISO YYYY-MM-DD from DatePickerRHF → MMyyyy expected by BE
		const iso = /^(\d{4})-(\d{2})-\d{2}$/.exec(date)
		if (iso) return `${iso[2]}${iso[1]}`
		return date // already MMyyyy from a server round-trip
	}

	const formattedDate = getFormattedDate(
		(date as unknown as DateValue).toDate('it-IT')
	)

	return `${formattedDate.monthDigit}${formattedDate.year}`
}

export function convertStateToBody(
	
	state: State,
	tableDefaults: Partial<VMModel['StartingValues']>,
	activeStep: number
): VMModel {
	
	const isActivityRetirement = state.data.Four.Activity === 'P'
	const isActivityRetirementAndDisabled =
		state.data.Four.Activity === 'P' && state.data.Four.Occupation === 'IV'

	return {
		Provenienza: state.data.Provenienza,
		EmailSalvaOfferta:state.data.EmailSalvaOfferta,
		currentStep: activeStep,
		blockedSelectedSond: state.blockedSelectedSond,
		isModify: false,
		isLanding: false,
		IdRichiesta: state.data.IdRichiesta,
		sessione: state.data.sessione ?? '',
		Disclaimer:state.data.Disclaimer ?? '',
		OTPGenerated: null,
		cfChecked: null,
		checkCFcustom: null,
		codiceCliente: state.data.codiceCliente,
		serviceDown: false,
		verifyOTP: state.data.verifyOTP,
		Step4SelectedPartialViews: null,
		Thanks: state.data.Thanks,
		StartingValues: {
			CodiceAffinity: tableDefaults.CodiceAffinity ?? '',
			 CodiceAffinityEsterno: tableDefaults.CodiceAffinityEsterno ?? false,
			Broker: tableDefaults.Broker ?? null,
			ProductType: tableDefaults.ProductType ?? null,
			Product: tableDefaults.Product ?? null,
			FinancialTable: tableDefaults.FinancialTable ?? null,
			FinancialTableEntry: tableDefaults.FinancialTableEntry ?? null,
			Amount: tableDefaults.Amount ?? '',
			InstallmentsNumber: tableDefaults.InstallmentsNumber ?? null,
			InstallmentAmount: tableDefaults.InstallmentAmount ?? '',
			Token: tableDefaults.Token ?? '',
			oGUID: tableDefaults.oGUID ?? '',
			NAG: tableDefaults.NAG ?? '',
			
			//oGUID: state.defaults.sbToken ?? '', //ADD 4.1 pass token on BE model
		},
		One: {
			Amount: state.data.One.Amount,
			MotivationSelected: state.data.One.MotivationSelected,
			
			MotivationDetailSelected: state.data.One.MotivationDetailSelected,
			DescrMotivazione: state.data.One.DescrMotivazione,

			Installments: state.data.One.Installments,
			SelectedInstallment: {
				...state.data.One.SelectedInstallment,
				ImpServiziFinanz: state.data.One.SelectedInstallment?.ImpServiziFinanz ?? 0,
				ImpServiziIdentinet: state.data.One.SelectedInstallment?.ImpServiziIdentinet ?? 0,
			},
			CocalfinID: Number(state.data.One.CocalfinID),
			//Insurance: state.servizi.insurance,
			Insurance: state.data.One.Insurance,
			InsuranceComparatore: state.data.One.InsuranceComparatore, //***modifica per add 4.1 */
			FinancialTable: state.data.One.FinancialTable,
			Prodotto: state.data.One.Prodotto,
			TipoProdotto: state.data.One.TipoProdotto,
			Emettitore: state.data.One.Emettitore,
			Provenienza: state.data.Provenienza,
			IdentinetEnabled: state.data.One.IdentinetEnabled,
			IsSecciVisible: state.data.One.IsSecciVisible,
		},
		Two: state.data.Two,
		Three: {
			...state.data.Three,
			ResidentFrom: formatToVMModelDate('032023'),
		},
		Four: {
			...state.data.Four,
			ActivityStartDate: formatToVMModelDate(
				state.data.Four.ActivityStartDate as unknown as DateValue
			),
			ActivityEndDate: formatToVMModelDate(
				state.data.Four.ActivityEndDate as unknown as DateValue
			),
			// defaults like this are set because selects and yup do not want to cooperate
			ContractType: isActivityRetirementAndDisabled
				? 'V'
				: defaultWhenEmpty(
						state.currentStep,
						state.data.Four.ContractType,
						'O'
				  ),
			Monthly:
				state.data.Four.Activity === 'A'
					? '12'
					: defaultWhenEmpty(state.currentStep, state.data.Four.Monthly, '13'),
			ActivitySector: isActivityRetirement
				? 'PRE'
				: defaultWhenEmpty(
						state.currentStep,
						state.data.Four.ActivitySector,
						'PRI'
				  ),
			CompanyName: isActivityRetirement ? 'INPS' : state.data.Four.CompanyName,
			CompanyAddress: isActivityRetirement
				? {
						Address: 'Via Ciro Il Grande',
						Comune: '7952',
						ComuneLabel: 'ROMA',
						CAP: '00144',
						Provincia: 'RM',
						ProvinciaLabel: 'ROMA',
						AddressNumber: '',
				  }
				: state.data.Four.CompanyAddress,
			CompanyPhone: isActivityRetirement
				? '06164164'
				: state.data.Four.CompanyPhone,
		},
		Seven: {
			...state.data.Seven,
			IdSondaggio: state.data.Seven.IdSondaggio,
			CompletSondaggio: false, // TODO: da vedere
			BenEsclCom: state.servizi.beneficiaryEscComm, // add 4.3 BUG FIX
			SondSelected: state.data.Seven.SondSelected,
			SondApprovato: state.data.Seven.SondApprovato,
			Beneficiario: state.servizi.heir, // mostra ben
			UltPreferenze: state.servizi.thirdParty,
			IdentinetActive: state.data.Seven.IdentinetActive,
			IdentinetContractDuration: state.data.Seven.IdentinetContractDuration || '0',
			SelectedInstallmentSond: {
				InstallmentsCount: state.data.Seven.SelectedInstallmentSond?.InstallmentsCount ?? 0,
				Amount: state.data.Seven.SelectedInstallmentSond?.Amount ?? 0,
				Tan: state.data.Seven.SelectedInstallmentSond?.Tan ?? 0,
				Taeg: state.data.Seven.SelectedInstallmentSond?.Taeg ?? 0,
				PrgSimul: state.data.Seven.SelectedInstallmentSond?.PrgSimul ?? 0,
				Erogato: state.data.Seven.SelectedInstallmentSond?.Erogato ?? 0,
				FinanziamLordo: state.data.Seven.SelectedInstallmentSond?.FinanziamLordo ?? 0,
				ImpServiziFinanz: state.data.Seven.SelectedInstallmentSond?.ImpServiziFinanz ?? 0,
				ImpServiziIdentinet: state.data.Seven.SelectedInstallmentSond?.ImpServiziIdentinet ?? 0,

				Interessi:state.data.Seven.SelectedInstallmentSond?.Interessi ?? 0,
				Bollo:state.data.Seven.SelectedInstallmentSond?.Bollo ?? 0,
				Rendiconto:state.data.Seven.SelectedInstallmentSond?.Rendiconto ?? 0,
				Istruttoria:state.data.Seven.SelectedInstallmentSond?.Istruttoria ?? 0,
				SpeseGestPrat:state.data.Seven.SelectedInstallmentSond?.SpeseGestPrat ?? 0,
				Dovuto:state.data.Seven.SelectedInstallmentSond?.Dovuto ?? 0,
				
			},
		},
	}
}

function useVMModel() {
	const [activeStep] = useAtom(currentStepAtom)

	//console.log("[useVMModel] render activeStep =", activeStep)
	const { state, dispatch } = useAppContext()
	const [step, setStep] = React.useState(activeStep) // tengo traccia dello step

//console.log("VMMODE",activeStep,"LOALESTEP",step)

	const req = `DbOperations/SaveStep${
		activeStep === 5 ? '7' :activeStep
	}` // we do this because the backend registers the step 'Servizi' as its 7th step for some reason

	// sendvm second parameter
	const [vm, sendVM] = useApi<VMModel>({
		request: req as APIRequest,
		sleeper: true,
		method: 'POST',
		shouldCache: false,
	})

const tableDefaults = state.data?.StartingValues ?? {
  CodiceAffinity: '',
  Broker: null,
  ProductType: null,
  Product: null,
  FinancialTable: null,
  Amount: '',
  InstallmentsNumber: null,
  InstallmentAmount: '',
  Token: '',
  oGUID: state.defaults?.sbToken ?? '',
  NAG:''
}

	useEffect(() => {
		// Condizione per eseguire la query quando currentStep è maggiore di 'step' e non è uguale a 6
		if (activeStep > step && activeStep !== 6) {
			const body = convertStateToBody(state, tableDefaults,activeStep)
			sendVM({ body })
			forceScrollHeight()

			// TODO add 4.1 Se siamo nell'ultimo step, nascondiamo la progress bar
			if (activeStep === Steps.RIEPILOGO) {
				dispatch({
					type: Actions.ShowUIProgressBar,
					payload: false,
				})
			}
		}

		// Impostiamo il nuovo step
		if (activeStep !== 6) {
			setStep(activeStep)
		}
	}, [activeStep])

	useEffect(() => {
		if (vm) {
			dispatch({
				type: Actions.UpdateData,
				payload: {
					...(vm as Omit<State['data'], 'Thanks'>),
					// Four: state.data.Four,
					sessione: vm.sessione.trim() === '' ? null : vm.sessione,
				},
			})

			forceScrollHeight()
			if (activeStep === Steps.RIEPILOGO) {
				dispatch({
					type: Actions.ShowUIProgressBar,
					payload: true,
				})
			}
			//add 4.3 quasndo cambiano le rate, nel save step1 viene reso il nuovo valore che si avviciona al valore delle rate selezionato
			//questo valore viene dispacciato nel service che controlla la logica e il model be
			if (activeStep === Steps.SERVIZI_FACOLTATIVI) { //@TODO
				dispatch({
					type: Actions.UpdateContractDuration,
					payload: vm.Seven.IdentinetContractDuration ?? '',
				})
			}
		}
	}, [vm])

	return vm
}

/* type Installment = {
	InstallmentsCount: number
	Amount: number
	Tan: number
	Taeg: number
	PrgSimul: number
	Erogato: number
	FinanziamLordo?: number
	ImpServiziFinanz?: number
	ImpServiziFinanzOLD: number
	ImpServiziIdentinet?: number
} */

type VMModel = {
	Provenienza: string
	IdRichiesta: number // chiedere
	currentStep: number
	isModify: boolean
	isLanding: boolean
	blockedSelectedSond: boolean
	sessione: string
	Disclaimer:string
	EmailSalvaOfferta:string | null
	Thanks: {
		done: boolean
	}
	StartingValues: {
		// parametri URL
		CodiceAffinity: string
		CodiceAffinityEsterno: boolean
		Broker: string | null
		ProductType: string | null
		Product: string | null
		FinancialTable: string | null
		FinancialTableEntry: string | null
		Amount: string
		InstallmentsNumber: string | null
		InstallmentAmount: string
		Token: string | null
		oGUID: string | null
		NAG:string | null
	}
	One: {
		Amount: string
		MotivationSelected: string
		MotivationDetailSelected: string
		DescrMotivazione: string
		Insurance: boolean
		InsuranceComparatore: boolean
		Installments: Array<Installment>
		SelectedInstallment: Partial<Installment>
		CocalfinID: number // progsimul-like
		FinancialTable: string // ^
		Prodotto: string
		TipoProdotto: string
		Emettitore: string
		Provenienza: string
		IdentinetEnabled: boolean
		IsSecciVisible: boolean
	}
	Two: {
		FiscalCode: string
		FiscalCodeCountry: string | null
		Name: string
		Surname: string
		IP: string // da prendere dopo step 1
		BirthDate: string
		BirthState: string
		BirthStateId: string // chiedere
		BirthLocation: string
		BirthLocationId: string // chiedere
		Gender: string
		Marketing: boolean | null
		Profilazione: boolean | null
		MarketingGBCI: boolean | null
		Third: boolean | null
		Privacy: boolean | null
		Sensible: boolean
		PEP: boolean // campo duplicato presente nell'oggetto four
		PhoneNumber: string
		CellNumber: string
		Email: string
		ConfirmEmail: string
		Session: null
	}
	Three: {
		Citizenship: string
		CitizenshipType: string
		CitizenshipExpire: string
		MaritalState: string
		HomeType: string
		RentAmount: string
		CurrentAddress: {
			Address: string
			AddressNumber: string
			CAP: string
			Comune: string
			ComuneLabel: string
			Provincia: string
			ProvinciaLabel: string
		}
		PrecAddress: {
			//  deprecato
			Address: string
			AddressNumber: string
			CAP: string
			Comune: string
			ComuneLabel: string
			Provincia: string
			ProvinciaLabel: string
		}
		ResidentFrom: string
		ResidentMonthFrom: string
		ResidentYearFrom: string
		Document: {
			DocumentType: string
			Serial: string
			DateOfIssue: string
			IssuingPlace: string
			Address: {
				Address: string
				AddressNumber: string
				CAP: string
				Comune: string
				ComuneLabel: string
				Provincia: string
				ProvinciaLabel: string
			}
		}
	}
	Four: {
		Activity: string
		Occupation: string
		ActivityStartDate: string
		ActivityEndDate: string
		ActivityStartMonth: string
		ActivityEndMonth: string
		ActivityStartYear: string
		ActivityEndYear: string
		ActivitySector: string
		NetMonthlyIncome: string
		ContractType: string
		Monthly: string
		CompanyName: string
		CompanyAddress: {
			Address: string
			AddressNumber: string
			CAP: string
			Comune: string
			ComuneLabel: string
			Provincia: string
			ProvinciaLabel: string
		}
		CompanyPhone: string
		CorrentistaBCC: boolean
		IBAN: string
		PEP: boolean
	}
	Seven: {
		IdSondaggio: number
		InstallmentsSond: Array<Installment>
		SelectedInstallmentSond: {
			InstallmentsCount: number
			Amount: number
			Tan: number
			Taeg: number
			PrgSimul: number
			Erogato: number
			FinanziamLordo: number
			ImpServiziFinanz: number
			ImpServiziIdentinet: number
			Interessi:number
			Bollo:number
			Rendiconto:number
			Istruttoria:number
			SpeseGestPrat:number
			Dovuto:number

		}
		Sondaggio: Survey['Sondaggio']
		nSelectedInstallmentSond: number
		MotivationSelected: string | null
		SondSelected: boolean // mostrato
		SondApprovato: boolean // esito positivo
		SondRisposte: string
		Beneficiario: boolean // mostra ben
		BenName: string
		BenSurname: string
		BenFiscalCode: string
		BenRelazCli: string
		BenCellNumber: string
		BenEmail: string
		BenAddress: {
			Address: string
			AddressNumber: string
			CAP: string
			Comune: string
			ComuneLabel: string
			Provincia: string
			ProvinciaLabel: string
		}
		BenBirthDate: string | null
		BenBirthState: string | null
		BenBirthStateId: string | null
		BenBirthLocation: string | null
		BenBirthLocationId: string | null
		BenGender: string | null
		BenEsclCom: boolean
		UltPreferenze: boolean
		UltPrfFiscalCode: string
		UltPrfName: string
		UltPrfSurname: string
		UltPrfCellNumber: string
		UltPrfEmail: string
		UltPrfAddress: {
			Address: string
			AddressNumber: string
			CAP: string
			Comune: string
			ComuneLabel: string
			Provincia: string
			ProvinciaLabel: string
		}
		UltPrfBirthDate: string | null
		UltPrfBirthState: string | null
		UltPrfBirthStateId: string | null
		UltPrfBirthLocation: string | null
		UltPrfBirthLocationId: string | null
		UltPrfGender: string | null
		CompletSondaggio: boolean
		BenPhoneNumber: string
		UltPrfPhoneNumber: string
		IdentinetActive: boolean
		IdentinetContractDuration: string | null
	}
	verifyOTP: boolean
	OTPGenerated: string | null
	codiceCliente: number | null // backend
	cfChecked: string | null // x
	Step4SelectedPartialViews: string | null // x
	serviceDown: boolean // x - mostrare messaggio errore down
	checkCFcustom: string | null // pointless as of now
}

export { useVMModel, type VMModel, type Installment }
